import {
  isFractionalAllowed,
  type ReceiptLineInput,
  type Unit,
} from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';

/**
 * Kirim satrining QORALAMASI — ekrandagi holat, so'rov tanasi emas.
 *
 * `qty` satr sifatida saqlanadi: odam `12.` yozib turgan paytda uni songa
 * o'girish maydonni orqasidan tuzatib qo'yadi va skaner oqimi uziladi. Songa
 * o'girish faqat yuborish oldidan, bitta joyda bo'ladi (`toLineInputs`).
 */
export interface ReceiptLineDraft {
  /** Faqat ro'yxat kaliti — serverga ketmaydi. */
  key: string;
  nomenclatureId: string | null;
  cellId: string | null;
  /** Odam ko'rgan/skanerlagan katak kodi — tekshiruv natijasi `cellId` da. */
  cellCode: string;
  qty: string;
}

export const emptyLine = (key: string, nomenclatureId: string | null = null): ReceiptLineDraft => ({
  key,
  nomenclatureId,
  cellId: null,
  cellCode: '',
  qty: '',
});

/**
 * Miqdorni o'qiydi. Vergul ham nuqta ham qabul qilinadi: klaviaturaning
 * raqamli blokida vergul chiqadigan tartib ham bor va odam buni bilmaydi.
 */
export const parseQty = (raw: string): number | null => {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Miqdor xatosi yoki `null`.
 *
 * Kasr taqiqi `isFractionalAllowed` dan olinadi (`@eombor/contracts`) —
 * ro'yxat frontendda TAKRORLANMAYDI, aks holda backend bilan bir kun
 * ajralib ketadi. Backend baribir o'zi ham tekshiradi (`assertQtyForUnit`).
 */
export const qtyError = (unit: Unit | null, raw: string): string | null => {
  if (raw.trim() === '') return null;
  const value = parseQty(raw);
  if (value === null) return t('stock.errorQty');
  if (!isFractionalAllowed(unit ?? 'DONA') && !Number.isInteger(value)) {
    return t('stock.errorFraction', { unit: unitText(unit ?? 'DONA') });
  }
  return null;
};

/** Satr yuborishga tayyor: nomenklatura, tekshirilgan katak va to'g'ri miqdor. */
export const isLineReady = (line: ReceiptLineDraft, unit: Unit | null): boolean =>
  line.nomenclatureId !== null &&
  line.cellId !== null &&
  parseQty(line.qty) !== null &&
  qtyError(unit, line.qty) === null;

export interface UnitTotal {
  unit: Unit;
  qty: number;
}

export interface LineTally {
  lineCount: number;
  /** Birlik bo'yicha jami — birliklar qo'shilmaydi (dona bilan kg emas). */
  totals: UnitTotal[];
}

/**
 * Jonli hisob: nechta tayyor satr va har birlikda qancha.
 *
 * Faqat TAYYOR satrlar sanaladi — yarim to'ldirilgan satr jamini
 * o'zgartirsa, skladchi ekrandagi raqamga ishonmay qoladi.
 */
export const tallyLines = (
  lines: ReceiptLineDraft[],
  unitOf: (nomenclatureId: string) => Unit | null,
): LineTally => {
  const totals = new Map<Unit, number>();
  let lineCount = 0;

  for (const line of lines) {
    if (line.nomenclatureId === null) continue;
    const unit = unitOf(line.nomenclatureId);
    if (unit === null || !isLineReady(line, unit)) continue;
    const value = parseQty(line.qty);
    if (value === null) continue;
    lineCount += 1;
    totals.set(unit, (totals.get(unit) ?? 0) + value);
  }

  return {
    lineCount,
    totals: [...totals.entries()].map(([unit, qty]) => ({ unit, qty })),
  };
};

/**
 * Qoralamani so'rov tanasiga o'giradi — TAYYOR satrlar bo'yicha.
 *
 * Yarim to'ldirilgan satr jimgina tashlab ketiladi, chunki u ekranda
 * "Tayyor emas" bo'lib turadi: saqlashda yo'qolgan satr kutilmagan bo'lmaydi.
 */
export const toLineInputs = (
  lines: ReceiptLineDraft[],
  unitOf: (nomenclatureId: string) => Unit | null,
): ReceiptLineInput[] => {
  const inputs: ReceiptLineInput[] = [];

  for (const line of lines) {
    const { nomenclatureId, cellId } = line;
    if (nomenclatureId === null || cellId === null) continue;
    const unit = unitOf(nomenclatureId);
    if (unit === null || !isLineReady(line, unit)) continue;
    const qty = parseQty(line.qty);
    if (qty === null) continue;
    inputs.push({ nomenclatureId, qty, cellId });
  }

  return inputs;
};

/** Yumaloqlash `numeric(14,3)` bilan bir xil — ekranda 480.0000001 chiqmaydi. */
export const formatQty = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** "12 satr · 480 dona · 24 kg" — o'ng yuqoridagi jonli hisob matni. */
export const tallyText = (tally: LineTally): string =>
  [
    t('stock.tallyLines', { count: tally.lineCount }),
    ...tally.totals.map((total) => `${formatQty(total.qty)} ${unitText(total.unit)}`),
  ].join(' · ');
